<script>
import DoughnutChart from '@components/charts/doughnut-chart.vue';
import moment from 'moment';

export default {
  components: {
    DoughnutChart,
  },
  props: {
    day: {
      type: Object,
      required: true,
      default: () => {},
    },
    hideBorder: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      chartData: {
        datasets: [
          {
            backgroundColor: ['#FACD0D', '#25EE9B', '#447EE2'],
            data: [this.dailyMacroPercentage('carbs'), this.dailyMacroPercentage('fat'), this.dailyMacroPercentage('protein')],
          },
        ],
      },
      chartOpts: {
        cutoutPercentage: 75,
        tooltips: {
          enabled: false,
        },
        legend: {
          display: false,
        },
      },
      hasMacros: false,
    };
  },
  computed: {
    dayName() {
      return this.day && this.day.dateString ? moment(this.day.dateString.split('T')[0]).format('ddd') : '';
    },
    dayNum() {
      return this.day && this.day.header ? this.day.header.substring(4) : '';
    },
    calories() {
      return this.day &&
        this.day.aggregatedNutritionDigest &&
        this.day.aggregatedNutritionDigest.macroNutritionDigest &&
        this.day.aggregatedNutritionDigest.macroNutritionDigest.calories &&
        this.day.aggregatedNutritionDigest.macroNutritionDigest.calories.value
        ? this.day.aggregatedNutritionDigest.macroNutritionDigest.calories.value
        : '—';
    },
    gramsOfFat() {
      return this.day &&
        this.day.aggregatedNutritionDigest &&
        this.day.aggregatedNutritionDigest.macroNutritionDigest &&
        this.day.aggregatedNutritionDigest.macroNutritionDigest.fat &&
        this.day.aggregatedNutritionDigest.macroNutritionDigest.fat.value
        ? `${this.day.aggregatedNutritionDigest.macroNutritionDigest.fat.value}g`
        : '—';
    },
    gramsOfCarbs() {
      return this.day &&
        this.day.aggregatedNutritionDigest &&
        this.day.aggregatedNutritionDigest.macroNutritionDigest &&
        this.day.aggregatedNutritionDigest.macroNutritionDigest.carbs &&
        this.day.aggregatedNutritionDigest.macroNutritionDigest.carbs.value
        ? `${this.day.aggregatedNutritionDigest.macroNutritionDigest.carbs.value}g`
        : '—';
    },
    gramsOfProtein() {
      return this.day &&
        this.day.aggregatedNutritionDigest &&
        this.day.aggregatedNutritionDigest.macroNutritionDigest &&
        this.day.aggregatedNutritionDigest.macroNutritionDigest.protein &&
        this.day.aggregatedNutritionDigest.macroNutritionDigest.protein.value
        ? `${this.day.aggregatedNutritionDigest.macroNutritionDigest.protein.value}g`
        : '—';
    },
  },
  mounted() {
    this.checkForMacros();
  },
  methods: {
    dailyMacroPercentage(macro) {
      if (
        this.day &&
        this.day.aggregatedNutritionDigest &&
        this.day.aggregatedNutritionDigest.macroNutritionDigest &&
        this.day.aggregatedNutritionDigest.macroNutritionDigest[macro]
      ) {
        return this.day.aggregatedNutritionDigest.macroNutritionDigest[macro].macroPercentage || 0;
      } else {
        return 0;
      }
    },
    checkForMacros() {
      if (this.day) {
        if (this.day.aggregatedNutritionDigest) {
          this.day.aggregatedNutritionDigest && this.day.aggregatedNutritionDigest.recipesWithNutrition
            ? (this.hasMacros = true)
            : (this.hasMacros = false);
        } else {
          const mealtimes = ['Breakfast', 'Lunch', 'Dinner'];
          mealtimes.forEach((mealtime) => {
            if (this.day[mealtime] && this.day[mealtime].aggregatedNutritionDigest.recipesWithNutrition > 0) {
              this.hasMacros = true;
            }
          });
        }
      }
    },
  },
};
</script>

<template>
  <div :class="['macros-card-mini', { 'no-border': hideBorder }]">
    <div class="divider">&nbsp;</div>

    <div class="macros-card-title">Macros</div>
    <div class="macros-card-mini-header">
      <div class="day">
        <span>{{ dayName }}</span>
        <span>{{ dayNum }}</span>
      </div>
      <div class="doughnut-chart-wrapper">
        <DoughnutChart v-if="hasMacros" class="doughnut-chart" :data="chartData" :options="chartOpts" />
        <div class="doughnut-chart-disabled" v-else>&nbsp;</div>
      </div>
    </div>
    <div class="macros-card-mini-content">
      <div class="macro-labels">
        <label>Calories</label>
        <label>Carbs</label>
        <label>Protein</label>
        <label>Fat</label>
      </div>
      <div class="macro-units">
        <label>{{ calories }}</label>
        <label>{{ gramsOfCarbs }}</label>
        <label>{{ gramsOfProtein }}</label>
        <label>{{ gramsOfFat }}</label>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.macros-card-mini {
  border-bottom: 1px solid $color-light-gray;
  border-right: 1px solid $color-light-gray;
  flex: 1;
  height: 100%;
  padding: 0 4rem 1rem;

  &.no-border {
    border: 0;
  }

  .macros-card-title {
    color: #888;
    font-size: 17.3529px;
    font-weight: bold;
    letter-spacing: 1.21922px;
    line-height: 2.4rem;
    margin-bottom: 10px;
    text-align: left;
    text-transform: uppercase;
  }

  .macros-card-mini-header,
  .macros-card-mini-content {
    display: flex;
    justify-content: space-between;
  }

  .macros-card-mini-header {
    height: 4.8rem;
    margin-bottom: 3.2rem;

    .day {
      color: #707070;
      font-size: 1.2rem;
      font-weight: 800;
      letter-spacing: 0.1px;
      line-height: 2.2rem;
      text-transform: uppercase;

      span {
        display: block;

        &:last-of-type {
          color: $color-heading-text;
          font-size: 2.1rem;
          font-weight: normal;
          letter-spacing: -1.6125px;
          line-height: 1;
        }
      }
    }

    .doughnut-chart-wrapper {
      height: auto;
      position: relative;
      width: 4.8rem;

      .doughnut-chart {
        position: absolute;
        width: 100%;
      }

      .doughnut-chart-disabled {
        border: 2px solid $color-light-gray;
        border-radius: 50%;
        height: 100%;
        width: 100%;
      }
    }
  }

  .macros-card-mini-content {
    .macro-labels label,
    .macro-units label {
      display: block;
    }

    .macro-units {
      text-align: right;

      label {
        color: $color-heading-text;
        font-size: 1.1rem;
        font-weight: 600;
        letter-spacing: -0.0523808px;
        text-transform: lowercase;

        &:first-of-type {
          font-size: 1.8rem;
          font-weight: 800;
          letter-spacing: 0.1px;
          line-height: 2.2rem;
        }
      }
    }
  }

  .divider {
    border-left: 1px solid $color-border-light-gray;
    bottom: 0;
    left: 0;
    position: absolute;
    top: 0;
  }
}
</style>
